import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { dataLibraryApi, fetchAllPages, MAX_GROUPED_ROWS } from '../../../services/api/dataLibrary';
import type { DataLibrarySessionSummaryParams } from '../../../services/api/dataLibrary';
import type { DataLibrarySessionRow, DataLibrarySessionFilterOptions, ColumnAverages } from '../../../types';
import type { SortConfig } from '../../../hooks/useColumnConfig';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import type { SortState } from '../../../utils/sortTasks';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { groupDataByLevels, type GroupNode } from '../../../utils/groupDataByField';
import { SESSIONS_COLUMNS } from './sessionsColumns';
import { getISOWeekMonday, getSessionMonth } from './SessionsCellRenderers';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { useDataLibraryColumns } from '../useDataLibraryTableLayout';

interface UseDataLibrarySessionsProps {
  selectedCohortIds: string[];
  /** MED-661: Initial filters when opened from drill-down */
  initialFilters?: ActiveFilterRow[];
}

export function useDataLibrarySessions({ selectedCohortIds, initialFilters }: UseDataLibrarySessionsProps) {
  // Data state
  const [data, setData] = useState<DataLibrarySessionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // MDF-41: server averages over all matching rows, by column id
  const [averages, setAverages] = useState<ColumnAverages>({});
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState<DataLibrarySessionFilterOptions>({
    groups: [],
    cohorts: [],
    coaches: [],
    sessionMonths: [],
    sessionNumbers: [],
  });

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Search
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // Sort (server-side) — default: session_date DESC
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: 'session_date',
    order: 'DESC',
  });

  // Filters
  const [activeFilters, setActiveFilters] = useState<ActiveFilterRow[]>(initialFilters ?? []);

  // Grouping (client-side)
  const [activeGroup, setActiveGroup] = useState<GroupState | null>(null);

  // MED-1200 (C2C / AC6): the airtime column is governed by live_session_delivery.
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');
  // AC6: "the column shows whenever the result set includes at least one
  // qualifying (LSD, group-coaching) cohort" — qualifying rows carry a non-null
  // airtime (null means the backend gated the row out). If nothing qualifies,
  // hide the column entirely rather than show a full column of "—".
  const hasQualifyingAirtime = useMemo(() => data.some(r => r.airtime !== null), [data]);

  // Column config
  const { columns, setColumns } = useDataLibraryColumns(SESSIONS_COLUMNS);
  const visibleColumns = useMemo(
    () =>
      columns.filter(c => {
        if (c.id === 'airtime') {
          return c.visible && liveSessionDeliveryEnabled && hasQualifyingAirtime;
        }
        return c.visible;
      }),
    [columns, liveSessionDeliveryEnabled, hasQualifyingAirtime],
  );

  // Build API params from state
  const buildParams = useCallback((): DataLibrarySessionSummaryParams => {
    const params: DataLibrarySessionSummaryParams = {
      cohortIds: selectedCohortIds,
      page,
      limit: pageSize,
      ...(sortConfig && { sortBy: sortConfig.key, sortOrder: sortConfig.order }),
    };

    if (debouncedSearch) params.search = debouncedSearch;

    // Active filters
    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      switch (filter.key) {
        case 'cohort':
          params.cohortId = filter.values[0];
          break;
        case 'group':
          params.groupId = filter.values.join(',');
          break;
        case 'coach':
          params.coachId = filter.values[0];
          break;
        case 'session_month':
          params.sessionMonth = filter.values.join(',');
          break;
        case 'session_number':
          params.sessionNumber = filter.values.join(',');
          break;
        case 'session_status': {
          const v = filter.values[0];
          if (v === 'completed' || v === 'upcoming' || v === 'flagged') {
            params.sessionStatus = v;
          }
          break;
        }
      }
    }

    return params;
  }, [
    selectedCohortIds,
    page,
    pageSize,
    sortConfig,
    debouncedSearch,
    activeFilters,
  ]);

  // MDF-39: the filtered total when it is over MAX_GROUPED_ROWS; the table then stays paged
  const [overGroupLimit, setOverGroupLimit] = useState<number | null>(null);
  const isGrouped = !!activeGroup && overGroupLimit === null;
  // MDF-39: only the newest request may write state; a grouped load spans many requests
  const latestRequest = useRef(0);

  // Fetch data
  const fetchData = useCallback(async () => {
    const requestId = ++latestRequest.current;
    if (selectedCohortIds.length === 0) {
      setData([]);
      setTotalCount(0);
      setAverages({});
      setTotalPages(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = buildParams();
      // MDF-39: a grouped table needs every row, so it fetches all pages
      const result = isGrouped
        ? await fetchAllPages((page, limit) => dataLibraryApi.getSessions({ ...params, page, limit }), () => requestId !== latestRequest.current)
        : await dataLibraryApi.getSessions(params);
      if (requestId !== latestRequest.current) return;
      const total = result.meta.pagination.total;
      setOverGroupLimit(total > MAX_GROUPED_ROWS ? total : null);
      if (isGrouped && total > MAX_GROUPED_ROWS) return;

      setData(result.data || []);
      setTotalCount(result.meta.pagination.total);
      setAverages(result.meta.averages);
      setTotalPages(result.meta.pagination.totalPages);
      setFilterOptions(result.meta.filterOptions);
    } catch {
      if (requestId !== latestRequest.current) return;
      notifications.show({
        title: 'Error',
        message: 'Failed to load session data',
        color: 'red',
      });
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [selectedCohortIds, buildParams, isGrouped]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page on filter/search change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeFilters, selectedCohortIds, activeGroup]);

  // Sort handlers
  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({
      key,
      order: prev?.key === key && prev.order === 'ASC' ? 'DESC' : 'ASC',
    }));
  }, []);

  const handleSortPanelChange = useCallback((sort: SortState | null) => {
    if (sort) {
      setSortConfig({ key: sort.field, order: sort.direction === 'asc' ? 'ASC' : 'DESC' });
    } else {
      setSortConfig(null);
    }
  }, []);

  // Filter handlers
  const handleFilterAdd = useCallback((key: string) => {
    setActiveFilters(prev => {
      if (prev.some(f => f.key === key)) return prev;
      return [...prev, { key, values: [] }];
    });
  }, []);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters(prev => prev.map(f => (f.key === key ? { ...f, values } : f)));
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    setActiveFilters(prev => prev.filter(f => f.key !== key));
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveFilters([]);
    setSearchValue('');
  }, []);

  const hasActiveFilters =
    activeFilters.some(f => f.values.length > 0) ||
    debouncedSearch.length > 0;

  // Enrich rows with computed fields for grouping (session_week, session_month)
  const enrichedData = useMemo(() =>
    data.map(row => ({
      ...row,
      session_week: row.session_date ? `Week of ${getISOWeekMonday(row.session_date)}` : '',
      session_month: row.session_date ? getSessionMonth(row.session_date).toUpperCase() : '',
    })),
    [data],
  );

  // Grouped data (client-side grouping)
  const groupedData = useMemo(() => {
    return groupDataByLevels(
      enrichedData as unknown as Record<string, unknown>[],
      isGrouped ? activeGroup : null,
      columns,
    ) as GroupNode<DataLibrarySessionRow>[] | null;
  }, [enrichedData, activeGroup, isGrouped, columns]);

  return {
    groupLimitTotal: activeGroup ? overGroupLimit : null,
    data,
    groupedData,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,

    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },

    searchValue,
    setSearchValue,

    sortConfig,
    handleSort,
    handleSortPanelChange,

    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,

    activeGroup,
    setActiveGroup,

    columns,
    setColumns,
    visibleColumns,

    fetchData,
  };
}
